import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { DatePickerItem } from "../../../components/forms/FormItems";
import {
  SettingsDataSelect,
  EmployeesSelect,
} from "../../../components/forms/AutoCompleteFormItem";
import { createReference, updateReference } from "../patientReferencesSlice";
import AddEntityForm from "../../../components/templates/AddEntityForm";
import { Space, Tag, Typography } from "antd";
import {
  getFullName,
  getLocalPhoneNumber,
} from "../../../components/util/helpers";

const { Text } = Typography;

const AddUpdateReference = ({ hide, addText, mode, record, activePatient }) => {
  const [activeEntity, setActiveEntity] = useState(record);
  const [referrerType, setReferrerType] = useState(null);
  const [chosenEmployee, setChosenEmployee] = useState(null);
  const [employeesList, setEmployeesList] = useState([]);

  const dispatch = useDispatch();
  const createLoading = useSelector(
    (state) => state.pendingActions["patientReferences/create"]
  );
  const updateLoading = useSelector(
    (state) => state.pendingActions["patientReferences/update"]
  );

  useEffect(() => {
    setActiveEntity(record);
  }, [record]);

  useEffect(() => {
    if (activeEntity?.employee_id) {
      const activeEmployee = employeesList.find(
        (i) => i.id === activeEntity.employee_id
      );
      setChosenEmployee(activeEmployee);
    } else {
      setChosenEmployee(null);
    }
  }, [activeEntity, employeesList]);

  const onFinish = async (values) => {
    let referenceValues = { ...values };
    if (values.sub_referrer_id) {
      referenceValues = { ...referenceValues, employee_id: null };
    } else if (values.employee_id) {
      referenceValues = { ...referenceValues, sub_referrer_id: null };
    }
    const params = {
      reference: referenceValues,
      branchId: activePatient.branch,
      patientId: activePatient.id,
      referenceId: mode === "edit" && record ? record.id : null,
    };

    const action =
      mode === "edit"
        ? await dispatch(updateReference(params))
        : await dispatch(createReference(params));

    if (!action.error) hide();
  };

  const onValuesChange = (values, form) => {
    if (activeEntity && values.referrer_id !== activeEntity.referrer_id) {
      form.setFieldsValue({ sub_referrer_id: null, employee_id: null });
    }
    setActiveEntity(values);
  };

  const filterItems = (data) => {
    return activeEntity?.referrer_id
      ? data.filter(
          (i) =>
            i.referrer_id === activeEntity.referrer_id &&
            i.branches.includes(activePatient.branch)
        )
      : [];
  };

  const filterReferrers = (items) => {
    const selected = items.find((i) => i.id === activeEntity?.referrer_id);
    setReferrerType(selected?.referrer_type);
    return items;
  };

  const filterEmployees = (items) => {
    setEmployeesList(items);
    return items;
  };

  const caregiverFilter = useMemo(
    () => ({
      $or: [
        {
          $and: [{ staff_member: { $neq: true } }, { status: { $eq: 1 } }],
        },
        ...(activeEntity?.employee_id
          ? [{ id: activeEntity.employee_id }]
          : []),
      ],
    }),
    [activeEntity?.employee_id]
  );

  const items = [
    {
      name: "reference_time",
      required: true,
      comp: (
        <DatePickerItem
          name="reference_time"
          category="references"
          required
          format="L"
        />
      ),
    },
    {
      name: "referrer_id",
      required: true,
      comp: (
        <SettingsDataSelect
          name="referrer_id"
          category="references"
          required
          settingsdataName="system_tables/referrer"
          filterItems={filterReferrers}
        />
      ),
    },
    ...(referrerType === "employees"
      ? [
          {
            name: "employee_id",
            required: true,
            comp: (
              <>
                <EmployeesSelect
                  name="employee_id"
                  category="references"
                  required
                  filterItems={filterEmployees}
                  extraParams={{ filter: caregiverFilter }}
                  showFullEmplyeeInfo
                />
                {chosenEmployee && (
                  <Space style={{ width: "100%", justifyContent: "center" }}>
                    <Text>
                      {`${getFullName(
                        chosenEmployee.surename,
                        chosenEmployee.first_name
                      )}
                     ${
                       chosenEmployee.local_id_number
                         ? `| ${chosenEmployee.local_id_number}`
                         : ""
                     }
                     ${
                       chosenEmployee?.contact_info?.mobile_number
                         ? `| ${getLocalPhoneNumber(
                             chosenEmployee.contact_info.mobile_number
                           )}`
                         : ""
                     }`}
                    </Text>
                    {chosenEmployee?.active_branches.map((branch) => (
                      <Tag key={branch.id} color="magenta">
                        {branch.name}
                      </Tag>
                    ))}
                  </Space>
                )}
              </>
            ),
          },
        ]
      : [
          {
            name: "sub_referrer_id",
            required: true,
            comp: (
              <SettingsDataSelect
                name="sub_referrer_id"
                category="references"
                required
                settingsdataName="system_tables/sub_referrer"
                filterItems={filterItems}
              />
            ),
          },
        ]),
  ];

  return (
    <AddEntityForm
      formLayout={{ labelCol: { span: 10 }, wrapperCol: { span: 12 } }}
      style={{ width: "700px" }}
      resource="patients"
      action="editor"
      activeEntity={record}
      items={items}
      onFinish={onFinish}
      onFinishFailed={console.error}
      onEntityValuesChange={onValuesChange}
      onDecline={hide}
      saveLoading={createLoading || updateLoading}
      addText={addText}
      mode={mode}
    />
  );
};

AddUpdateReference.propTypes = {
  hide: PropTypes.func.isRequired,
  addText: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  mode: PropTypes.string.isRequired,
  record: PropTypes.object,
  activePatient: PropTypes.object.isRequired,
};

export default AddUpdateReference;
